/**
 * The setup guide's steps and when it shows (section 6). The guide is decided once everything its
 * steps read is in (Home's data and the brand, its changes and its items); until then the top of
 * Home is a placeholder, so a set-up brand never flashes an unfinished guide and the layout does
 * not jump. If any of those never arrives, Home shows what needs attention without the guide after
 * a few seconds. Dismissing is remembered per brand; "Show setup guide" brings it back.
 *
 * While the guide shows, a publish problem that one of its steps clears (no Home screen yet) is
 * that step's job: it is marked on the step, not raised as an alarm beside it.
 */

import { useEffect, useState } from "react";
import type { Problem } from "@/api/admin";
import { href, itemRoute, type Route } from "@/lib/route";
import { setupStepOf, type SetupStep } from "@/publishing/problems";
import type { Studio } from "@/shell/studio";
import { lower, noun } from "@/vocabulary/vocabulary";
import type { HomeData } from "./use-home-data";

export interface Step { id: SetupStep; title: string; body: string; done: boolean; action: { label: string; href: string }; /** A publish problem waits on this step. */ needed?: boolean }

export interface SetupGuideState {
  steps: Step[];
  /** The top of Home can be drawn (the guide is decided, or it gave up waiting). */
  topReady: boolean;
  /** Everything the guide reads is in. */
  studioReady: boolean;
  setupDone: boolean;
  guideShows: boolean;
  dismissed: boolean;
  dismiss: () => void;
  show: () => void;
  /** Steps that a publish problem waits on (marked on the step while the guide shows). */
  stepProblems: Set<SetupStep | undefined>;
  /** Publish problems that are not a step's job while the guide shows: Needs attention names them. */
  otherErrors: Problem[];
}

const dismissKey = (brand: string) => `viewform-console-setup-dismissed:${brand}`;
const readDismissed = (brand: string) => { try { return localStorage.getItem(dismissKey(brand)) === "1"; } catch { return false; } };

export function useSetupGuide(studio: Studio, data: HomeData | null): SetupGuideState {
  const { client, pack, changes, counts, brandView, items } = studio;
  const brand = client.brand.id;
  const [dismissed, setDismissed] = useState(() => readDismissed(brand));
  useEffect(() => setDismissed(readDismissed(brand)), [brand]);
  const link = (r: Omit<Route, "brand">) => href({ brand, ...r } as Route);

  const titles = data?.full.filter((e) => e.type === "title") ?? [];
  const hasArtwork = titles.some((t) => Object.keys((t.data["artwork"] as Record<string, unknown> | undefined) ?? {}).length > 0);
  const steps: Step[] = [
    { id: "brand", title: "Check your brand", body: "Your name, colour and menu, as the apps show them.", done: !!brandView && (brandView.revision > 0 || !!brandView.publishedUrl), action: { label: "Open brand settings", href: link({ section: "settings", page: "brand" }) } },
    { id: "item", title: `Add your first ${lower(noun(pack, "title"))}`, body: `A ${lower(noun(pack, "title"))} is a video in your app, with its name and details.`, done: (counts.title ?? 0) > 0, action: { label: `Add a ${lower(noun(pack, "title"))}`, href: link({ section: "library", type: "title", show: "new" }) } },
    { id: "artwork", title: "Add artwork", body: "A poster or a wide image, so it stands out in the rows.", done: hasArtwork, action: { label: "Add artwork", href: titles[0] ? link(itemRoute(brand, "title", titles[0].id)) : link({ section: "library", type: "title" }) } },
    { id: "screen", title: "Set up a screen", body: "Choose the rows your app's Home screen shows.", done: (counts.page ?? 0) > 0 && (brandView?.manifest.menu.length ?? 0) > 0, action: { label: "Open your app", href: link({ section: "app" }) } },
    { id: "publish", title: "Publish", body: "Send it all to the apps. You'll see every change first.", done: !!changes?.live, action: { label: "Review & publish", href: link({ section: "review" }) } },
  ];

  const errors = data?.errors ?? [];
  const studioReady = changes !== null && items !== null && brandView !== null;
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    if (!data || studioReady) return;
    const t = window.setTimeout(() => setGaveUp(true), 5000);
    return () => window.clearTimeout(t);
  }, [data, studioReady]);
  const topReady = !!data && (studioReady || gaveUp);
  const setupDone = steps.every((s) => s.done);
  const guideShows = !!data && studioReady && !dismissed;
  const stepProblems = new Set(errors.map((p) => setupStepOf(p.message)).filter((id) => id && steps.some((s) => s.id === id && !s.done)));
  for (const s of steps) s.needed = stepProblems.has(s.id);
  const otherErrors = guideShows ? errors.filter((p) => !stepProblems.has(setupStepOf(p.message))) : errors;

  return {
    steps, topReady, studioReady, setupDone, guideShows, dismissed, stepProblems, otherErrors,
    dismiss: () => { try { localStorage.setItem(dismissKey(brand), "1"); } catch { /* the guide comes back next time */ } setDismissed(true); },
    show: () => { try { localStorage.removeItem(dismissKey(brand)); } catch { /* shown for this visit */ } setDismissed(false); },
  };
}
